/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Box, Stack, Pressable } from '@atlaskit/primitives/compiled';
import { useEffect, useMemo, useState } from 'react';

import { jsx, keyframes } from '@compiled/react';
import { cssMap } from '@atlaskit/css';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { fg } from '@atlaskit/platform-feature-flags';

import Heading from '@atlaskit/heading';
import Button from '@atlaskit/button/new';
import { ConfluenceLogo } from '@atlaskit/logo';
import { token } from '@atlaskit/tokens';
import { ExperienceStart } from '@atlassian/experience-tracker';
import LoginToConfluenceIconUrl from '../assets/LoginPageIconSVG.svg';
import { ExperienceNames } from '../experience-tracker/ExperienceNames';
import { type useUrl } from './useUrl';
import { useUniqueKey } from '../unique-key-provider/UniqueKeyProvider';

import RefreshBanner from './RefreshBanner';
import messages from './messages';
import { usePerformanceMetrics } from '../performance-metrics/performanceMetricsProvider';
import ConnectLoomToConfluenceSVGUrl from '../assets/ConnectLoomToConfluenceSVG.svg';
import Text from '@atlaskit/primitives/text';

const fadeIn = keyframes({
	from: {
		opacity: 0,
	},
	to: {
		opacity: 1,
	},
});

const styles = cssMap({
	loginButtonWrapper: { marginTop: token('space.400') },
	parentDiv: {
		display: 'inline-grid',
		alignItems: 'center',
		justifyItems: 'center',
	},
	refreshBannerFade: {
		paddingTop: token('space.250'),
		paddingRight: token('space.250'),
		paddingBottom: token('space.250'),
		paddingLeft: token('space.250'),
		animationName: fadeIn,
		animationDuration: '3s',
	},
	loomHeading: {
		font: token('font.heading.large'),
		fontWeight: token('font.weight.semibold'),
		color: token('color.text'),
	},
	loomLoginButton: {
		width: '392px',
		height: '56px',
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('color.background.brand.bold'),
		color: token('color.text.inverse'),
		font: token('font.body.large'),
		fontWeight: token('font.weight.semibold'),
		transition: 'background-color 0.2s ease-in-out',
		'&:hover': {
			backgroundColor: token('color.background.brand.bold.hovered'),
			transition: 'background-color 0.2s ease-in-out',
		},
		'&:active': {
			backgroundColor: token('color.background.brand.bold.pressed'),
		},
	},
});

export type LoginPageProps = Pick<
	Parameters<typeof useUrl>[0],
	'contentId' | 'spaceKey' | 'parentProduct'
> & {
	reloadEP: () => void;
	url?: string | null;
};

type LoginPageVariantProps = {
	contentId: string;
	displayRefreshBanner: boolean;
	formatMessage: (message: MessageDescriptor) => string;
	handleClick: () => void;
	reloadEP: () => void;
	spaceKey?: string;
};

const SUCCESS_URL_SUFFIX = '/wiki/embed/login-confirmation';

export const LoginPage = ({
	contentId,
	parentProduct,
	reloadEP,
	spaceKey,
	url,
}: LoginPageProps): JSX.Element => {
	const { locale, formatMessage } = useIntl();

	const { uniqueKey } = useUniqueKey();

	const { cancelIframeLoadMetrics } = usePerformanceMetrics();

	useEffect(() => {
		cancelIframeLoadMetrics();
	}, [cancelIframeLoadMetrics]);

	const loginUrl = useMemo(() => {
		const origin = url ? new URL(url).origin : window?.location?.origin;
		const uniqueKeyParam = uniqueKey ? `&uniqueKey=${uniqueKey}` : '';
		return `${origin}/login?application=confluence&dest-url=${encodeURIComponent(
			`${SUCCESS_URL_SUFFIX}?locale=${locale}`,
		)}${uniqueKeyParam}`;
	}, [url, locale, uniqueKey]);

	const isLoomParentProduct =
		parentProduct === 'loom' && fg('mr2-meeting-recording-notes-confluence');

	const handleClick = () => {
		openLoginPopup(loginUrl);
		setDisplayRefreshBanner(!displayRefreshBanner);
	};

	const [displayRefreshBanner, setDisplayRefreshBanner] = useState(false);

	return isLoomParentProduct ? (
		<LoomLoginPage
			contentId={contentId}
			formatMessage={formatMessage}
			handleClick={handleClick}
			displayRefreshBanner={displayRefreshBanner}
			reloadEP={reloadEP}
			spaceKey={spaceKey}
		/>
	) : (
		<BaseLoginPage
			contentId={contentId}
			formatMessage={formatMessage}
			handleClick={handleClick}
			displayRefreshBanner={displayRefreshBanner}
			reloadEP={reloadEP}
			spaceKey={spaceKey}
		/>
	);
};

export const LoomLoginPage = ({
	contentId,
	displayRefreshBanner,
	formatMessage,
	handleClick,
	reloadEP,
	spaceKey,
}: LoginPageVariantProps): JSX.Element => {
	return (
		<div css={styles.parentDiv} data-testid="login-page-test-id">
			<ExperienceStart name={ExperienceNames.LOGIN_PAGE} id={`${spaceKey}-${contentId}-loom`} />
			<Stack space="space.400" alignInline="center">
				<Stack space="space.200" alignInline="center">
					<img
						src={ConnectLoomToConfluenceSVGUrl}
						alt={formatMessage(messages.connectLoomToConfluenceImageAriaLabel)}
						width="167"
						height="110"
					/>
					<Stack space="space.100" alignInline="center">
						<Heading as="h2" size="large">
							<Box xcss={styles.loomHeading}>
								{formatMessage(messages.connectToConfluenceHeading)}
							</Box>
						</Heading>
						<Text color="color.text.subtlest" size="medium">
							{formatMessage(messages.connectToConfluenceDescription)}
						</Text>
					</Stack>
				</Stack>
				<Pressable
					xcss={styles.loomLoginButton}
					onClick={handleClick}
					testId="connect-confluence-button"
					aria-label={formatMessage(messages.connectConfluenceButtonAriaLabel)}
				>
					{formatMessage(messages.connectConfluenceButton)}
				</Pressable>
			</Stack>
			{displayRefreshBanner && (
				<div css={styles.refreshBannerFade}>
					<RefreshBanner
						reloadEP={reloadEP}
						title={formatMessage(messages.refreshBanner)}
						actionLabel={formatMessage(messages.refreshBannerLink)}
					/>
				</div>
			)}
		</div>
	);
};

export const BaseLoginPage = ({
	contentId,
	displayRefreshBanner,
	formatMessage,
	handleClick,
	reloadEP,
	spaceKey,
}: LoginPageVariantProps): JSX.Element => {
	return (
		<div css={styles.parentDiv} data-testid="login-page-test-id">
			<ExperienceStart name={ExperienceNames.LOGIN_PAGE} id={`${spaceKey}-${contentId}`} />
			<Stack space="space.250" alignInline="center">
				<ConfluenceLogo size="large" appearance="brand" />
				<img
					src={LoginToConfluenceIconUrl}
					alt={formatMessage(messages.loginToConfluenceIconAriaLabel)}
					width="148"
					height="160"
				/>
				<Heading as="h2" size="large">
					{formatMessage(messages.loginText)}
				</Heading>
			</Stack>
			<Box xcss={styles.loginButtonWrapper}>
				<Button
					testId="login-button"
					aria-label={formatMessage(messages.loginAriaLabel)}
					appearance="primary"
					onClick={handleClick}
				>
					{formatMessage(messages.loginButton)}
				</Button>
			</Box>
			{displayRefreshBanner && (
				<div css={styles.refreshBannerFade}>
					<RefreshBanner
						reloadEP={reloadEP}
						title={formatMessage(messages.refreshBanner)}
						actionLabel={formatMessage(messages.refreshBannerLink)}
					/>
				</div>
			)}
		</div>
	);
};

function openLoginPopup(loginUrl: string) {
	const windowFeatures = 'popup=yes,left=100,top=100,width=640,height=640';
	window.open(loginUrl, '_blank', windowFeatures);
}
